import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    getMaterialSatisfactionOverview,
    listMaterialSatisfaction,
    MaterialSatisfactionParams,
    MaterialSatisfactionRow,
    SatGroupRow,
    SatOverview,
    SatScope,
} from '../../api/assets';
import { ApiError } from '../../api/request';
import Pagination from '../../components/Pagination';
import SearchSelect from '../../components/SearchSelect';
import HelpTip from '../../components/HelpTip';
import { useSyncQueryToUrl } from '../../utils/urlState';
import { useCategoryText } from '../../utils/categoryText';
import { BarList, DateRangeInput, SegGroup, StatCard } from './parts';
import { satReasonText } from './procLocalText';
import PrismSyncBar from './PrismSyncBar';
import {
    ComboTable, CompositionBar, MaterialCell, MIN_RELIABLE_N, MonthlyBars, RateBarRow, RateBars, RateText, Section,
} from './satisfactionParts';
import { fmtN, pct } from './satisfactionText';

// 数据资产·满意度统计 (2026-10-23) — 飞书《官方材料加工次数 + 满意度》两个需求合成一页看板 + 明细:
//   需求一 加工次数: 官方材料 × 设备 × 瓦数/光源 的累计上报条数, 不区分用户, Studio 实时 + XCS 2.7 历史合并;
//   需求二 满意度:   满意 / (满意 + 不满意), 未评价不进分母, 先按维度累加再算比例. 两套口径可切:
//                   「不限参数」= 全部有评价的加工;「严格」= 只看采纳官方推荐参数的加工 (需求原文, 目前样本极少).
// 设备口径同《首次加工成功率》: 设备 = 机型 × 激光模组 (瓦数 + 光源), 同型号换瓦数/光源即为不同设备.
// 看板与明细共用一套筛选 (统计日期 / 口径 / 材料 / 分类 / 机型 / 设备); 明细一行一个材料, 点开看各设备.
// 数据来自后端进程内快照 (随 prism 同步每小时更新).

const PAGE_SIZE = 20;

type SortKey = NonNullable<MaterialSatisfactionParams['sort']>;

const SCOPE_OPTS = [
    { value: 'all', label: '不限参数' },
    { value: 'adopted', label: '严格 · 采纳推荐参数' },
];
const SORT_OPTS = [
    { value: 'process_count', label: '加工次数' },
    { value: 'rated', label: '样本量' },
    { value: 'rate', label: '满意度' },
];
const ORDER_OPTS = [
    { value: 'desc', label: '高→低' },
    { value: 'asc', label: '低→高' },
];
const MIN_RATED_OPTS = [
    { value: '1', label: '样本 ≥ 1' },
    { value: '10', label: '样本 ≥ 10' },
    { value: '30', label: '样本 ≥ 30' },
];
const WORST_MIN_OPTS = [
    { value: '5', label: 'n≥5' },
    { value: '10', label: 'n≥10' },
    { value: '30', label: 'n≥30' },
    { value: '100', label: 'n≥100' },
];

function toRateRows(rows: SatGroupRow[], label: (r: SatGroupRow) => string, onClick?: (r: SatGroupRow) => void): RateBarRow[] {
    return rows.map((r) => ({
        ...r, key: r.key || '(空)', label: label(r), sub: r.sub || undefined,
        onClick: onClick ? () => onClick(r) : undefined,
    }));
}

function MaterialSatisfaction() {
    const initQ = new URLSearchParams(useLocation().search);
    const catText = useCategoryText();
    const [scope, setScope] = useState<SatScope>(initQ.get('scope') === 'adopted' ? 'adopted' : 'all');
    const [keyword, setKeyword] = useState(initQ.get('keyword') || '');
    const [keywordDraft, setKeywordDraft] = useState(keyword);
    const [type1, setType1] = useState(initQ.get('type1') || '');
    const [deviceName, setDeviceName] = useState(initQ.get('device_name') || '');
    const [deviceModule, setDeviceModule] = useState(initQ.get('device_module') || '');
    const [minRated, setMinRated] = useState(initQ.get('min_rated') || '');
    const [dateFrom, setDateFrom] = useState(initQ.get('date_from') || '');
    const [dateTo, setDateTo] = useState(initQ.get('date_to') || '');
    const [worstMin, setWorstMin] = useState(initQ.get('worst_min') || '30');
    const [sort, setSort] = useState<SortKey>((initQ.get('sort') as SortKey) || 'process_count');
    const [order, setOrder] = useState<'asc' | 'desc'>(initQ.get('order') === 'asc' ? 'asc' : 'desc');
    const [page, setPage] = useState(Number(initQ.get('page')) || 1);
    const [tab, setTab] = useState<'stats' | 'table'>(initQ.get('tab') === 'table' ? 'table' : 'stats');

    const [ov, setOv] = useState<SatOverview | null>(null);
    const [rows, setRows] = useState<MaterialSatisfactionRow[]>([]);
    const [total, setTotal] = useState(0);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useSyncQueryToUrl('/assets/material-satisfaction', {
        tab: tab === 'stats' ? undefined : tab,
        scope: scope === 'all' ? undefined : scope,
        keyword: keyword || undefined,
        type1: type1 || undefined,
        device_name: deviceName || undefined,
        device_module: deviceModule || undefined,
        min_rated: minRated || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        worst_min: worstMin === '30' ? undefined : worstMin,
        sort: sort === 'process_count' ? undefined : sort,
        order: order === 'desc' ? undefined : order,
        page: page > 1 ? page : undefined,
    });

    const filterKey = JSON.stringify({ scope, keyword, type1, deviceName, deviceModule, minRated, dateFrom, dateTo });
    const filters = useMemo(() => {
        const f = JSON.parse(filterKey) as Record<string, string>;
        return {
            scope: f.scope as SatScope, keyword: f.keyword || undefined, type1: f.type1 || undefined,
            device_name: f.deviceName || undefined, device_module: f.deviceModule || undefined,
            min_rated: f.minRated ? Number(f.minRated) : undefined,
            date_from: f.dateFrom || undefined, date_to: f.dateTo || undefined,
        };
    }, [filterKey]);

    const loadOverview = useCallback(async () => {
        try {
            setOv(await getMaterialSatisfactionOverview({ ...filters, worst_min: Number(worstMin) }));
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
        }
    }, [filters, worstMin]);

    const loadList = useCallback(async () => {
        setLoading(true);
        try {
            const res = await listMaterialSatisfaction({ ...filters, sort, order, page, pageSize: PAGE_SIZE });
            setRows(res.data || []);
            setTotal(res.total);
            setError('');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
            setRows([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    }, [filters, sort, order, page]);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => { if (tab === 'table') loadList(); }, [loadList, tab]);
    const onSynced = useCallback(() => { loadOverview(); loadList(); }, [loadOverview, loadList]);

    // 各筛选改了都回第一页.
    const withReset = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };
    const setDates = (from: string, to: string) => { setDateFrom(from); setDateTo(to); setPage(1); };
    const resetFilters = () => {
        setKeyword(''); setKeywordDraft(''); setType1(''); setDeviceName(''); setDeviceModule(''); setMinRated('');
        setDateFrom(''); setDateTo(''); setPage(1);
    };
    const toggle = (xcode: string) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(xcode)) next.delete(xcode); else next.add(xcode);
        return next;
    });
    const allOpen = rows.length > 0 && rows.every((r) => expanded.has(r.xcode));

    const cur = ov ? (scope === 'adopted' ? ov.summary.adopted : ov.summary.all) : null;
    const overall = cur?.satisfaction_rate ?? null;
    const officialShare = useMemo(() => {
        if (!ov) return null;
        const all = ov.composition.reduce((a, r) => a + r.process_count, 0);
        const off = ov.composition.find((r) => r.kind === 'official')?.process_count || 0;
        return all ? off / all : null;
    }, [ov]);
    const chips = [
        (dateFrom || dateTo) && { k: '日期', v: `${dateFrom || '最早'} ~ ${dateTo || '最新'}`, clear: () => { setDateFrom(''); setDateTo(''); } },
        keyword && { k: '材料', v: keyword, clear: () => { setKeyword(''); setKeywordDraft(''); } },
        type1 && { k: '分类', v: catText(type1), clear: () => setType1('') },
        deviceName && { k: '机型', v: deviceName, clear: () => setDeviceName('') },
        deviceModule && { k: '设备', v: deviceModule, clear: () => setDeviceModule('') },
        minRated && { k: '样本', v: `≥ ${minRated}`, clear: () => setMinRated('') },
    ].filter(Boolean) as { k: string; v: string; clear: () => void }[];
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const scopeName = scope === 'adopted' ? '严格口径 (采纳推荐参数)' : '不限参数';

    return (
        <div className="space-y-5">
            {/* 页头 (与「材料加工」同款) */}
            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="text-[10px] font-extrabold tracking-[4px] uppercase text-slate-400 mb-1">Material · Satisfaction</div>
                    <div className="text-xl font-extrabold text-slate-900 tracking-tight">满意度统计</div>
                    <div className="text-xs text-slate-500 mt-1.5">
                        官方材料被加工了多少次、用户满不满意，按设备拆开看。
                        {ov?.generated_at ? <span className="text-slate-400"> 数据快照 {new Date(ov.generated_at * 1000).toLocaleString()}</span> : null}
                    </div>
                </div>
                <PrismSyncBar onSynced={onSynced} />
            </div>

            {/* tabs: 统计 (看板) / 明细; 筛选两边共用 */}
            <div className="flex items-center gap-1">
                {([['stats', '统计'], ['table', '材料 × 设备明细']] as const).map(([key, label]) => (
                    <button key={key} type="button"
                        className={`px-[15px] py-1.5 rounded-[9px] text-[13px] font-semibold ${tab === key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        onClick={() => setTab(key)}>
                        {label}
                    </button>
                ))}
            </div>

            {/* 统计日期 (与「材料加工」同款; 看板与明细共用, 不选 = 全部数据) */}
            <div className="card px-4 py-2.5 flex flex-wrap items-center gap-3">
                <span className="text-xs text-slate-400">统计日期</span>
                <DateRangeInput from={dateFrom} to={dateTo} onChange={setDates} presets className="w-[620px] max-w-full" />
                <span className="ml-auto text-[11px] text-slate-400">
                    {dateFrom || dateTo ? `${dateFrom || '最早'} ~ ${dateTo || '最新'}（UTC，含首尾两天）` : '未选日期：统计全部数据'}
                </span>
            </div>

            {/* 筛选: 看板与明细共用 */}
            <div className="card p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                    <SegGroup label="满意度口径" options={SCOPE_OPTS} value={scope} onChange={(v) => withReset(setScope)(v as SatScope)}
                        title="不限参数 = 全部有评价的加工; 严格 = 只看采纳了官方推荐参数的加工 (需求原文口径, 只有走材料识别的加工判得出, 样本少)" />
                    <input
                        className="inp"
                        style={{ width: 180 }}
                        placeholder="搜 材料编码 / 名称"
                        value={keywordDraft}
                        onChange={(e) => setKeywordDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') withReset(setKeyword)(keywordDraft.trim()); }}
                    />
                    <SearchSelect className="w-36" value={type1} onChange={withReset(setType1)} placeholder="一级分类 (全部)"
                        options={(ov?.categories || []).map((c) => ({ value: c, label: catText(c) }))} clearable />
                    <SearchSelect className="w-40" value={deviceName} onChange={withReset(setDeviceName)} placeholder="机型 (全部)"
                        options={(ov?.options.device_names || []).map((d) => ({ value: d.value, label: `${d.value || '未知'} (${fmtN(d.process_count)})` }))} clearable />
                    <SearchSelect className="w-52" value={deviceModule} onChange={withReset(setDeviceModule)} placeholder="设备 · 瓦数/光源 (全部)"
                        options={(ov?.options.device_modules || [])
                            .filter((d) => !deviceName || d.value === deviceName || d.value.startsWith(`${deviceName} · `))
                            .map((d) => ({ value: d.value, label: `${d.value || '未知'} (${fmtN(d.process_count)})` }))} clearable />
                    <SearchSelect className="w-32" value={minRated} onChange={withReset(setMinRated)} placeholder="样本量 (不限)"
                        options={MIN_RATED_OPTS} clearable />
                    <button type="button" className="btn-ghost" onClick={() => withReset(setKeyword)(keywordDraft.trim())}>搜索</button>
                    <button type="button" className="btn-ghost" onClick={resetFilters}>重置</button>
                </div>
                {chips.length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        {chips.map((c) => (
                            <span key={c.k} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                                {c.k}: {c.v}
                                <button type="button" className="text-slate-400 hover:text-slate-700" onClick={withReset(c.clear)}>✕</button>
                            </span>
                        ))}
                        <button type="button" className="text-brand-600 hover:underline ml-1" onClick={resetFilters}>清除全部</button>
                    </div>
                )}
            </div>

            {error && <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>}

            {tab === 'stats' && (
                <>
            {/* 总览 */}
            <div className="grid grid-cols-5 gap-4">
                <StatCard label="官方材料加工次数" value={ov ? fmtN(ov.summary.process_count) : '—'}
                    sub={officialShare != null ? `占全部加工 ${pct(officialShare)} (来源构成只随日期)` : undefined} />
                <StatCard label="覆盖" value={ov ? `${ov.summary.materials.toLocaleString()} 个材料` : '—'}
                    sub={ov ? `${ov.summary.devices} 个机型 · ${ov.summary.device_modules} 个设备 · ${ov.summary.combos.toLocaleString()} 个组合` : undefined} />
                <StatCard label={`满意度 · ${scope === 'adopted' ? '严格' : '不限参数'}`} value={pct(overall)} accent="text-emerald-600"
                    sub={cur ? `满意 ${cur.satisfied.toLocaleString()} · 不满意 ${cur.unsatisfied.toLocaleString()} · n=${cur.rated.toLocaleString()}` : undefined} />
                <StatCard label="两套口径对照" value={ov ? `${pct(ov.summary.all.satisfaction_rate)} / ${pct(ov.summary.adopted.satisfaction_rate)}` : '—'}
                    sub={ov ? `不限 n=${ov.summary.all.rated.toLocaleString()} · 严格 n=${ov.summary.adopted.rated.toLocaleString()}` : undefined} />
                {/* 分母 = 该口径下可评的加工 (严格: 采纳了推荐参数的; 不限: 全部) = 已评 + 未评 */}
                <StatCard label="评价覆盖率" value={cur ? pct(cur.rated + cur.unrated ? cur.rated / (cur.rated + cur.unrated) : null, 2) : '—'} accent="text-slate-600"
                    sub={cur ? `未评价 ${fmtN(cur.unrated)} 次 · 不进分母` : undefined} />
            </div>

            <div className="text-[12px] text-slate-500 card px-4 py-2.5 leading-relaxed">
                <b className="text-slate-700">口径</b>：数据 = Studio 实时上报（prism）+ XCS 2.7 历史加工数据，<b>不含神策</b>。
                加工次数 = 上报条数累加，不区分用户；满意度 = 满意 ÷（满意 + 不满意），先按维度累加再相除，未评价不进分母。
                「严格」只算 Studio 上报「参数采纳 = 采纳」的加工（需求原文口径；只有走了材料识别的加工判得出，样本很少），
                「不限参数」算全部有评价的加工。样本量 &lt; {MIN_RELIABLE_N} 淡显「样本不足」，低于当前范围整体 3 个百分点以上标红。
                官方材料 = 材料 id 能对上平台材料主数据的 legacy_mid（热压域除外）。
            </div>

            {ov && (
                <>
                    <Section no="01" title="加工记录的材料来源构成"
                        desc="需求只统计官方材料 (绿色段); 其余三类不进下面的材料统计. 本块只随统计日期变化, 不随其他筛选">
                        <CompositionBar rows={ov.composition} />
                    </Section>

                    <div className="grid grid-cols-2 gap-4">
                        <Section no="02" title={`加工最多的官方材料 Top ${ov.top_materials.length}`}
                            desc={`按材料汇总 (所选设备范围内), 按加工次数排; 占比 = 该材料加工次数 ÷ 筛选范围内官方材料加工次数; 满意度为${scopeName}, 低于整体 3 个点标「偏低」`}>
                            <div className="space-y-1.5">
                                {/* 列头: 与下方行同宽对齐 */}
                                <div className="flex items-center gap-3 text-[11px] text-slate-400 pb-1 border-b border-slate-100">
                                    <div className="w-5 text-right">#</div>
                                    <div className="w-44 shrink-0">官方材料</div>
                                    <div className="flex-1">加工次数 (条长相对第 1 名)</div>
                                    <div className="w-20 text-right">加工次数</div>
                                    <div className="w-12 text-right" title="该材料加工次数 ÷ 筛选范围内官方材料加工次数">占比</div>
                                    <div className="w-28 text-right" title={`满意 ÷ (满意 + 不满意), ${scopeName}`}>满意度</div>
                                </div>
                                {ov.top_materials.map((m, i) => {
                                    const share = ov.summary.process_count ? m.process_count / ov.summary.process_count : 0;
                                    const max = ov.top_materials[0]?.process_count || 1;
                                    return (
                                        <div key={m.xcode} className="flex items-center gap-3 text-xs">
                                            <div className="w-5 text-right text-slate-400 tabular-nums">{i + 1}</div>
                                            <div className="w-44 shrink-0"><MaterialCell xcode={m.xcode} name={m.material_name} type1={m.type1} catText={catText} /></div>
                                            <div className="flex-1 h-3 rounded bg-slate-100 relative" title={`${m.process_count.toLocaleString()} 次 · ${m.devices} 个设备`}>
                                                <div className="absolute left-0 top-0 h-3 rounded bg-emerald-500" style={{ width: `${(m.process_count / max) * 100}%` }} />
                                            </div>
                                            <div className="w-20 text-right tabular-nums text-slate-700">{fmtN(m.process_count)}</div>
                                            <div className="w-12 text-right tabular-nums text-slate-400">{pct(share)}</div>
                                            <div className="w-28 text-right"
                                                title={`满意 ${m.satisfied.toLocaleString()} · 不满意 ${m.unsatisfied.toLocaleString()} · 样本 n=${m.rated.toLocaleString()}`}>
                                                <RateText c={m} overall={overall} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </Section>
                        <Section no="03" title="按一级分类" desc={`${scopeName} · 点击行筛选该分类`}>
                            <RateBars overall={overall} labelWidth="w-28"
                                rows={toRateRows(ov.by_type1, (r) => catText(r.key) || '未分类', (r) => withReset(setType1)(r.key))} />
                        </Section>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Section no="04" title="按机型" desc={`${scopeName} · 机型下合并全部瓦数/光源 · 点击行筛选该机型`}>
                            <RateBars overall={overall} labelWidth="w-28"
                                rows={toRateRows(ov.by_device, (r) => r.label || '未知', (r) => withReset(setDeviceName)(r.key))} />
                        </Section>
                        <Section no="05" title="按设备 × 瓦数 / 光源"
                            desc={`${scopeName} · 同型号换瓦数/光源分开统计; 未上报模组的记录归在机型名下 · 点击行筛选该设备`}>
                            <RateBars overall={overall} labelWidth="w-44"
                                rows={toRateRows(ov.by_device_module, (r) => r.label || '未知', (r) => withReset(setDeviceModule)(r.key))
                                    .map((r) => ({ ...r, sub: r.sub || '未上报' }))} />
                        </Section>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Section no="06" title="加工次数最多的 材料 × 设备 × 瓦数/光源" desc={<>需求一的输出粒度; 完整组合见 <button type="button" className="text-brand-600 hover:underline" onClick={() => setTab('table')}>明细 tab</button> (展开材料行)</>}>
                            <ComboTable rows={ov.top_combos} overall={overall} catText={catText} />
                        </Section>
                        <Section no="07" title="月度趋势" desc="官方材料加工次数与占比、官方材料满意度; 按月汇总, 只随统计日期变化 (首尾月按所选日期截断, 当月为截至今天)">
                            <MonthlyBars rows={ov.monthly} />
                        </Section>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <Section no="08" title="低分材料" desc={`${scopeName} · 样本 ≥ ${ov.worst_min} 的材料按满意度从低到高`}
                            right={<SegGroup options={WORST_MIN_OPTS} value={worstMin} onChange={setWorstMin} />}>
                            <ComboTable rows={ov.low_materials} overall={overall} catText={catText} withDevice={false}
                                empty={`没有样本 ≥ ${ov.worst_min} 的材料, 可调低门槛`} />
                        </Section>
                        <Section no="09" title="组合重灾区" desc={`${scopeName} · 样本 ≥ ${ov.worst_min} 的 材料 × 设备 组合按满意度从低到高`}
                            right={<SegGroup options={WORST_MIN_OPTS} value={worstMin} onChange={setWorstMin} />}>
                            <ComboTable rows={ov.worst_combos} overall={overall} catText={catText}
                                empty={`没有样本 ≥ ${ov.worst_min} 的组合, 可调低门槛`} />
                        </Section>
                    </div>

                    <BarList title="不满意原因" labelWidth="w-36"
                        hint={deviceName || deviceModule ? '已按所选机型 / 设备过滤' : '全部设备'}
                        note="用户评价为不满意时勾选的原因 (可多选); 源数据只到设备粒度, 不分材料, 也不分口径"
                        rows={ov.reasons.map((r) => ({ label: satReasonText(r.code), value: r.count, title: r.code }))} />
                </>
            )}
                </>
            )}

            {/* 明细: 一行一个材料, 点开看各设备 */}
            {tab === 'table' && (<>
            <div className="card">
                <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
                    <div className="text-sm font-semibold text-slate-700">材料 × 设备明细</div>
                    <div className="text-xs text-slate-400">一行 = 一个官方材料 (所选范围合计) · 点 ▸ 展开该材料在各设备 (瓦数/光源) 下的数据</div>
                    <div className="ml-auto flex items-center gap-2">
                        <button type="button" className="btn-ghost text-xs" disabled={rows.length === 0}
                            onClick={() => setExpanded(allOpen ? new Set() : new Set(rows.map((r) => r.xcode)))}>
                            {allOpen ? '全部收起' : '本页全部展开'}
                        </button>
                        <SegGroup label="排序" options={SORT_OPTS} value={sort} onChange={(v) => withReset(setSort)(v as SortKey)} />
                        <SegGroup options={ORDER_OPTS} value={order} onChange={(v) => withReset(setOrder)(v as 'asc' | 'desc')} />
                        <span className="text-xs text-slate-400">{loading ? '加载中…' : `共 ${total.toLocaleString()} 个材料`}</span>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-[13px]" style={{ minWidth: 980 }}>
                        <thead>
                            <tr className="text-left text-xs text-slate-400 border-b border-slate-100 whitespace-nowrap">
                                <th className="pl-3 py-2.5 w-6" />
                                <th className="px-3 py-2.5 font-medium">官方材料 / 设备</th>
                                <th className="px-3 py-2.5 font-medium">瓦数 / 光源</th>
                                <th className="px-3 py-2.5 font-medium text-right">加工次数</th>
                                <th className="px-3 py-2.5 font-medium text-right">满意数</th>
                                <th className="px-3 py-2.5 font-medium text-right">不满意数</th>
                                <th className="px-3 py-2.5 font-medium text-right">
                                    未评价数 <HelpTip text="有加工但没有评价 (严格口径下为采纳了推荐参数但没评价), 只做样本观察, 不进满意度分母" />
                                </th>
                                <th className="px-3 py-2.5 font-medium text-right">样本量</th>
                                <th className="px-3 py-2.5 font-medium">满意度</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={9} className="px-3 py-10 text-center text-slate-400">{loading ? '加载中…' : '暂无数据'}</td>
                                </tr>
                            ) : rows.map((r) => {
                                const open = expanded.has(r.xcode);
                                return (
                                    <React.Fragment key={r.xcode}>
                                        <tr className="border-b border-slate-50 hover:bg-slate-50 whitespace-nowrap cursor-pointer" onClick={() => toggle(r.xcode)}>
                                            <td className="pl-3 py-2 text-slate-400 select-none">{open ? '▾' : '▸'}</td>
                                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                                                <MaterialCell xcode={r.xcode} name={r.material_name} type1={r.type1} catText={catText} />
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-400">{r.devices.length} 个设备</td>
                                            <td className="px-3 py-2 text-right tabular-nums font-medium">{r.process_count.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{r.satisfied.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-rose-500">{r.unsatisfied.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right tabular-nums text-slate-400">{r.unrated.toLocaleString()}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{r.rated.toLocaleString()}</td>
                                            <td className="px-3 py-2"><RateText c={r} overall={overall} /></td>
                                        </tr>
                                        {open && r.devices.map((d) => (
                                            <tr key={`${r.xcode}|${d.device_module}`} className="bg-slate-50/60 border-b border-slate-50 whitespace-nowrap text-[12px]">
                                                <td />
                                                <td className="px-3 py-1.5 pl-8 text-slate-700">{d.device_name || '未知'}</td>
                                                <td className="px-3 py-1.5 text-slate-500">{d.module || '未上报'}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{d.process_count.toLocaleString()}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums text-emerald-600">{d.satisfied.toLocaleString()}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums text-rose-500">{d.unsatisfied.toLocaleString()}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-400">{d.unrated.toLocaleString()}</td>
                                                <td className="px-3 py-1.5 text-right tabular-nums">{d.rated.toLocaleString()}</td>
                                                <td className="px-3 py-1.5"><RateText c={d} overall={overall} /></td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="flex items-center justify-end text-xs text-slate-500">
                <Pagination page={page} totalPages={totalPages} onChange={setPage} total={total} />
            </div>
            </>)}
        </div>
    );
}

export default MaterialSatisfaction;
